import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getUserProgress, getUserProgressUserTrail, IUserProgressTrail } from 'services/user';
import { getCourse } from 'services/course';
import Course from 'services/course/models/course-interface';
import Trail from 'services/course/models/trail-interface';
import {
  OverallProgressContainer,
  ProgressHeader,
  CourseProgressGrid,
  CourseProgressCard,
  VideoPreviewSection,
  ContinueButton
} from './style';
import ImgWoman1 from 'assets/details/woman-draw-1period.svg';
import ImgWoman2 from 'assets/details/woman-draw-2period.svg';
import ImgWoman3 from 'assets/details/woman-draw-3period.svg';
import ImgWoman4 from 'assets/details/woman-draw-4period.svg';
import PlayIconWhiteOutline from 'assets/icon/icone-play-white.svg';

interface IOverallProgress {
  trail: Trail;
}

interface ILastWatchedInfo {
  trailId: string;
  courseId: string;
  lessonId: string;
  thumbnail: string;
  courseTitle: string;
  lessonTitle: string;
}

const OverallProgress: React.FC<IOverallProgress> = ({ trail }) => {
  const [userProgress, setUserProgress] = useState<IUserProgressTrail>();
  const [courseProgress, setCourseProgress] = useState<any[]>([]);
  const [lastWatchedInfo, setLastWatchedInfo] = useState<ILastWatchedInfo | null>(null);
  const history = useHistory();

  const createFormattedObject = (
    courses: Course[],
    progress: IUserProgressTrail,
  ): void => {
    let sortedCourses: any = progress.courses.map(course => {
      let courseFormat = courses.find(
        (courseSet: Course) => courseSet.course_id === course.course_id,
      );

      let contentLesson = courseFormat?.lessons.filter(
        lesson => lesson.alreadyFinished && lesson.alreadyStarted,
      );

      if (courseFormat) {
        const watched = contentLesson?.length || 0;
        const total = courseFormat.lessons.length || 1;
        const percent = watched / total;

        return {
          ...course,
          position: courseFormat.position,
          content_watched: `${watched}/${total}`,
          percent,
        };
      }

      return null;
    });

    if (sortedCourses && sortedCourses.length) {
      sortedCourses = sortedCourses.sort(
        (a: any, b: any) => a?.position - b?.position,
      );
    }

    setCourseProgress(sortedCourses);
  };

  // Função para buscar informações do último vídeo assistido
  const getLastWatchedInfo = async (): Promise<void> => {
    try {
      const progress = await getUserProgressUserTrail();

      if (progress && progress.length) {
        const localTrail = await getCourse(progress[0].trail_id);
        
        // Encontra o último curso iniciado (não finalizado)
        const sortCourse = localTrail.user.courses
          .filter(course => !!course.start_at && !!course.finish_at === false)
          .sort((a, b) => {
            let date1 = new Date(a.start_at);
            let date2 = new Date(b.start_at);

            if (date1 < date2) return 1;
            if (date1 > date2) return -1;
            return 0;
          });

        let lastCourse;

        if (sortCourse.length) {
          lastCourse = localTrail.courses.find(
            course => course.course_id === sortCourse[0].course_id,
          );
        }

        if (sortCourse && sortCourse.length) {
          // Encontra a última aula assistida
          let lastLessonProgress = sortCourse[0]?.contents
            .filter(lesson => !!lesson.start_at)
            .sort((a, b) => {
              let date1 = new Date(a.start_at);
              let date2 = new Date(b.start_at);

              if (date1 < date2) return 1;
              if (date1 > date2) return -1;
              return 0;
            });

          let lastLesson;

          if (lastCourse && lastLessonProgress && lastLessonProgress.length) {
            lastLesson = lastCourse.lessons.find(
              lesson => lesson.content_id === lastLessonProgress[0].content_id,
            );
          }

          // Se encontrou a última aula, salva as informações
          if (lastLesson && lastCourse) {
            setLastWatchedInfo({
              trailId: localTrail.trail_id,
              courseId: lastCourse.course_id,
              lessonId: lastLesson.content_id,
              thumbnail: lastLesson.thumbnail || '',
              courseTitle: lastCourse.title,
              lessonTitle: lastLesson.title,
            });
          }
        }
      }
    } catch (error) {
      console.error('Erro ao buscar informações do último vídeo:', error);
    }
  };

  useEffect(() => {
    (async () => {
      const localProgress = await getUserProgress(trail.trail_id);
      createFormattedObject(trail.courses, localProgress);
      setUserProgress(localProgress);
      
      // Busca informações do último vídeo assistido
      await getLastWatchedInfo();
    })();
  }, [trail.trail_id]);

  const handleReturnImg = (index: number): string => {
    switch (index) {
      case 0:
        return ImgWoman1;
      case 1:
        return ImgWoman2;
      case 2:
        return ImgWoman3;
      case 3:
        return ImgWoman4;
      default:
        return ImgWoman1;
    }
  };

  const getCourseTitle = (index: number): string => {
    const titles = ['1º trimestre', '2º trimestre', '3º trimestre', 'Cuidados'];
    return titles[index] || `${index + 1}º trimestre`;
  };

  const getWatchedLessons = (course: any): number => {
    const [watched] = course.content_watched.split('/').map(Number);
    return watched;
  };

  const getTotalLessons = (course: any): number => {
    const [, total] = course.content_watched.split('/').map(Number);
    return total;
  };

  // Cálculo do progresso geral (todas as aulas)
  const calculateOverallProgress = (courses: any[]) => {
    if (!courses || !courses.length) return 0;

    const totalWatched = courses.reduce((sum, c) => {
      const [watched] = c.content_watched.split('/').map(Number);
      return sum + watched;
    }, 0);

    const totalLessons = courses.reduce((sum, c) => {
      const [, total] = c.content_watched.split('/').map(Number);
      return sum + total;
    }, 0);

    return totalLessons > 0 ? totalWatched / totalLessons : 0;
  };

  // Função para continuar de onde parou
  const handleContinueWatching = (): void => {
    if (lastWatchedInfo) {
      history.push(
        `/trilha/${lastWatchedInfo.trailId}/curso/${lastWatchedInfo.courseId}/aula/${lastWatchedInfo.lessonId}`,
      );
    }
  };

  const overallPercent = calculateOverallProgress(courseProgress);

  return (
    <>
      {userProgress ? (
        <OverallProgressContainer>
          <div className="main-content">
            <div className="left-section">
              <ProgressHeader>
                <h1>JÁ ASSISTIDO</h1>
                <div className="overall-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${overallPercent * 100}%` }}
                    ></div>
                  </div>
                  <span className="progress-percentage">
                    {Math.round(overallPercent * 100)}% DO CURSO
                  </span>
                </div>
              </ProgressHeader>

              <CourseProgressGrid>
                {courseProgress && courseProgress.length
                  ? courseProgress.map((course: any, index: number) => (
                      <CourseProgressCard key={index}>
                        <div className="icon-container">
                          <div className="progress-circle">
                            <div
                              className="progress-fill"
                              style={{
                                background:
                                  course.percent > 0
                                    ? `conic-gradient(var(--primary-color) ${course.percent * 360}deg, #f0f0f0 0deg)`
                                    : '#f0f0f0',
                              }}
                            >
                              <div className="inner-circle">
                                <img src={handleReturnImg(index)} alt="ícone do curso" />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="course-info">
                          <h3>{getCourseTitle(index)}</h3>
                          <p>
                            {getWatchedLessons(course)}/{getTotalLessons(course)} aulas
                          </p>
                        </div>
                      </CourseProgressCard>
                    ))
                  : null}
              </CourseProgressGrid>
            </div>

            {/* Seção do vídeo com informações reais do último assistido */}
            <VideoPreviewSection>
              <div className="video-container">
                {lastWatchedInfo?.thumbnail ? (
                  <div className="video-thumbnail" onClick={handleContinueWatching}>
                    <img 
                      src={lastWatchedInfo.thumbnail} 
                      alt={lastWatchedInfo.lessonTitle}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="overlay"></div>
                    <div className="play-button">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="24" cy="24" r="24" fill="#d82e88" />
                        <path d="M20 16L32 24L20 32V16Z" fill="white" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div className="video-placeholder">
                    <div className="play-button">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="24" cy="24" r="24" fill="#d82e88" />
                        <path d="M20 16L32 24L20 32V16Z" fill="white" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              <ContinueButton onClick={handleContinueWatching}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="#d82e88">
                  <path d="M8 5L15 10L8 15V5Z" />
                </svg>
                CONTINUAR DE ONDE PAREI
              </ContinueButton>

            </VideoPreviewSection>
          </div>
        </OverallProgressContainer>
      ) : null}
    </>
  );
};

export default OverallProgress;